import React, { useState, useEffect } from "react";
import {
    Plus,
    Eye,
    Edit2,
    Trash2,
    Search,
    Filter,
    X,
    AlertCircle,
    User,
    Calendar,
    Mail,
    Phone,
    MapPin,
    CreditCard,
    Clock,
    ChevronRight
} from "lucide-react";
import "./MasterStudent.css";
import { fetchStudentList, createStudent, updateStudent, deleteStudent } from "../../services/studentService";
import { fetchBranchList, fetchBranchCourseList, fetchBatchList } from "../../services/masterService";

const MasterStudent = () => {
    const [students, setStudents] = useState([]);
    const [branches, setBranches] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });
    const [errors, setErrors] = useState({});
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [showMappingDropdown, setShowMappingDropdown] = useState(false);
    const [activeFilter, setActiveFilter] = useState({ type: "All", id: null, label: "All Students" });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const getTodayDate = () => new Date().toISOString().split('T')[0];

    const initialForm = {
        name: "",
        dob: "",
        branchCourseId: "",
        joiningDate: getTodayDate(),
        fees: "",
        feeRecurrence: "Monthly",
        currentProgressLevel: "Level-1",
        grade: "",
        batchId: "",
        roleId: 3,
        Parentname: "",
        password: "",
        email: "",
        phoneNumber: ""
    };

    const [formData, setFormData] = useState(initialForm);
    const [pin, setPin] = useState(["", "", "", ""]);
    const [branchCourseList, setBranchCourseList] = useState([]);
    const [batches, setBatches] = useState([]);

    useEffect(() => {
        loadData();
        loadMappingData();
    }, []);

    const loadMappingData = async () => {
        try {
            const [bcRes, batchRes] = await Promise.all([
                fetchBranchCourseList(),
                fetchBatchList()
            ]);
            setBranchCourseList(bcRes.data || []);
            setBatches(batchRes.data || []);
        } catch (err) {
            console.error("Failed to load mapping data", err);
        }
    };

    const loadData = async () => {
        try {
            setIsLoading(true);
            const studentRes = await fetchStudentList();
            console.log("studentRes", studentRes);
            setStudents(studentRes.data || []);
        } catch (err) {
            console.error("Load failed", err);
            showPopup("Error", typeof err === "string" && err ? err : "Failed to load students", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const showPopup = (title, message, type = "success") => {
        setPopup({ show: true, title, message, type });
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        return dateString.split('T')[0];
    };

    const handleAddClick = async () => {
        setFormData(initialForm);
        setPin(["", "", "", ""]);
        setIsEditMode(false);
        setIsViewMode(false);

        // Mapping data is now loaded in useEffect
        setErrors({});
        setShowAddModal(true);
    };

    const handleView = (item) => {
        setSelectedItem(item);
        setFormData({
            name: item.Name || "",
            dob: formatDateForInput(item.DateOfBirth || item.DOB || ""),
            branchCourseId: item.BranchCourseId || "",
            joiningDate: formatDateForInput(item.JoiningDate || ""),
            fees: item.Fees || "",
            feeRecurrence: item.FeeRecurrence || "Monthly",
            currentProgressLevel: item.CurrentProgressLevel || "",
            grade: item.Grade || "",
            batchId: item.BatchId || "",
            roleId: item.RoleId || 3,
            Parentname: item.Parentname || item.ParentName || item.UserName || "",
            password: "",
            email: item.Email || "",
            phoneNumber: item.PhoneNumber || ""
        });
        setPin(["*", "*", "*", "*"]);
        setIsViewMode(true);
        setIsEditMode(false);
        setErrors({});
        setShowAddModal(true);
    };

    const handleEditClick = (item) => {
        setSelectedItem(item);
        setFormData({
            name: item.Name || "",
            dob: formatDateForInput(item.DateOfBirth || item.DOB || ""),
            branchCourseId: item.BranchCourseId || "",
            joiningDate: formatDateForInput(item.JoiningDate || ""),
            fees: item.Fees || "",
            feeRecurrence: item.FeeRecurrence || "Monthly",
            currentProgressLevel: item.CurrentProgressLevel || "",
            grade: item.Grade || "",
            batchId: item.BatchId || "",
            roleId: item.RoleId || 3,
            Parentname: item.Parentname || item.ParentName || item.UserName || "",
            password: "",
            email: item.Email || "",
            phoneNumber: item.PhoneNumber || ""
        });
        setIsEditMode(true);
        setIsViewMode(false);
        setErrors({});
        setShowAddModal(true);
    };

    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\d{10}$/;
        const nameRegex = /^[a-zA-Z\s.]{2,50}$/;

        // Student Name
        if (!formData.name?.trim()) {
            newErrors.name = "Student Name is required";
        } else if (!nameRegex.test(formData.name)) {
            newErrors.name = "Letters and spaces only (2-50 chars)";
        }

        // DOB
        // if (!formData.dob) newErrors.dob = "Date of Birth is required";

        // Branch/Course Mapping
        if (!formData.branchCourseId) newErrors.branchCourseId = "Branch & Course are required";

        // Batch
        if (!formData.batchId) newErrors.batchId = "Batch selection is required";

        // Parent Name (Optional, auto-generated if empty)
        if (formData.Parentname?.trim() && !nameRegex.test(formData.Parentname)) {
            newErrors.Parentname = "Letters and spaces only";
        }

        // Phone Number
        if (!formData.phoneNumber) {
            newErrors.phoneNumber = "Phone Number is required";
        } else if (!phoneRegex.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "Must be exactly 10 digits";
        }

        // Email (Optional)
        if (formData.email?.trim() && !emailRegex.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }

        // Password (Add Mode only, auto-generated if empty)
        if (!isEditMode) {
            if (formData.password && formData.password.toString().length < 4) {
                newErrors.password = "Min 4 digits required";
            }
        }

        // Fees
        if (!formData.fees || parseFloat(formData.fees) <= 0) {
            newErrors.fees = "Valid Fees amount is required";
        }

        // Grade (Optional)
        // if (!formData.grade) {
        //     newErrors.grade = "Grade/Standard is required";
        // }

        // Joining Date
        if (!formData.joiningDate) {
            newErrors.joiningDate = "Joining Date is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePinChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;

        const newPin = [...pin];
        newPin[index] = value;
        setPin(newPin);

        // Sync with formData
        const combinedPin = newPin.join("");
        setFormData(prev => ({ ...prev, password: combinedPin }));

        // Clear error if any
        if (errors.password) {
            setErrors(prev => ({ ...prev, password: "" }));
        }

        // Auto-focus logic
        if (value && index < 3) {
            document.getElementById(`reg-pin-${index + 1}`)?.focus();
        }
    };

    const handlePinKeyDown = (e, index) => {
        if (e.key === "Backspace" && !pin[index] && index > 0) {
            document.getElementById(`reg-pin-${index - 1}`)?.focus();
        }
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showPopup("Validation Error", "Please fill all mandatory fields correctly", "error");
            return;
        }

        try {
            setIsLoading(true);
            if (isEditMode) {
                const updatePayload = {
                    StudentId: selectedItem.StudentId,
                    Name: formData.name,
                    DateOfBirth: formData.dob,
                    Fees: parseFloat(formData.fees) || 0,
                    FeeRecurrence: formData.feeRecurrence,
                    CurrentProgressLevel: formData.currentProgressLevel,
                    Grade: formData.grade,
                    BranchCourseId: parseInt(formData.branchCourseId),
                    BatchId: parseInt(formData.batchId),
                    Email: formData.email,
                    PhoneNumber: formData.phoneNumber,
                    JoiningDate: formData.joiningDate,
                    ParentName: formData.Parentname?.trim() ? formData.Parentname : `${formData.name}'s Parent`,
                    // UserId: selectedItem.UserId
                };
                console.log(updatePayload);
                await updateStudent(updatePayload);
                showPopup("Success", "Student updated successfully!");
            } else {
                const createPayload = {
                    Name: formData.name,
                    DateOfBirth: formData.dob,
                    BranchCourseId: parseInt(formData.branchCourseId),
                    JoiningDate: formData.joiningDate,
                    Fees: parseFloat(formData.fees) || 0,
                    FeeRecurrence: formData.feeRecurrence,
                    CurrentProgressLevel: formData.currentProgressLevel,
                    Grade: formData.grade,
                    BatchId: parseInt(formData.batchId),
                    RoleId: 3,
                    ParentName: formData.Parentname?.trim() ? formData.Parentname : `${formData.name}'s Parent`,
                    Password: formData.password ? parseInt(formData.password) : 1234,
                    Email: formData.email,
                    PhoneNumber: formData.phoneNumber
                };
                await createStudent(createPayload);
                showPopup("Success", "Student created successfully!");
            }
            loadData();
            setShowAddModal(false);
        } catch (err) {
            console.error("Save Error:", err);
            const errorMsg = typeof err === "string" ? err : (err?.response?.data?.message || err?.message || "An error occurred during save");
            showPopup("Submission Failed", errorMsg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            setIsLoading(true);
            await deleteStudent(selectedItem.StudentId);
            showPopup("Deleted", "Student has been removed successfully.");
            loadData();
            setShowDeleteModal(false);
        } catch (err) {
            showPopup("Error", typeof err === "string" && err ? err : "Failed to delete student", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredStudents = students.filter(s => {
        const matchesSearch = s.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.UserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.PhoneNumber?.includes(searchTerm);

        if (activeFilter.type === "All") return matchesSearch;
        if (activeFilter.type === "Batch") return matchesSearch && String(s.BatchId) === String(activeFilter.id);
        if (activeFilter.type === "Course") return matchesSearch && String(s.CourseName) === String(activeFilter.id);
        if (activeFilter.type === "Branch") return matchesSearch && String(s.BranchName) === String(activeFilter.id);

        return matchesSearch;
    });

    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const paginatedStudents = filteredStudents.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Reset to page 1 on search
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const checkIfDirty = () => {
        if (!showAddModal || isViewMode) return false;

        let initial;
        if (isEditMode) {
            initial = {
                name: selectedItem.Name || "",
                dob: selectedItem.DOB || "",
                branchCourseId: selectedItem.BranchCourseId || "",
                joiningDate: selectedItem.JoiningDate || "",
                fees: selectedItem.Fees || "",
                feeRecurrence: selectedItem.FeeRecurrence || "Monthly",
                currentProgressLevel: selectedItem.CurrentProgressLevel || "",
                grade: selectedItem.Grade || "",
                batchId: selectedItem.BatchId || "",
                roleId: selectedItem.RoleId || 3,
                Parentname: selectedItem.Parentname || selectedItem.ParentName || selectedItem.UserName || "",
                password: "",
                email: selectedItem.Email || "",
                phoneNumber: selectedItem.PhoneNumber || ""
            };
        } else {
            initial = initialForm;
        }

        return JSON.stringify(formData) !== JSON.stringify(initial);
    };

    const handleCloseModal = () => {
        if (checkIfDirty()) {
            setShowDiscardModal(true);
        } else {
            setShowAddModal(false);
        }
    };

    const confirmDiscard = () => {
        setShowDiscardModal(false);
        setShowAddModal(false);
    };

    return (
        <div className="master-student-container">
            {/* HEADER */}
            <div className="student-header">
                <div className="header-left">
                    <div className="modern-filter-selector" onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
                        <Filter size={18} className="filter-icon" />
                        <div className="current-filter">{activeFilter.label}</div>
                        <X
                            size={14}
                            className={`clear-filter ${activeFilter.type !== "All" ? "visible" : ""}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveFilter({ type: "All", id: null, label: "All Students" });
                            }}
                        />

                        {showFilterDropdown && (
                            <div className="filter-dropdown-menu">
                                <div
                                    className={`filter-option ${activeFilter.type === "All" ? "active" : ""}`}
                                    onClick={() => { setActiveFilter({ type: "All", id: null, label: "All Students" }); setShowFilterDropdown(false); }}
                                >
                                    All Students
                                </div>

                                <div className="filter-group-label">By Batch</div>
                                {batches.slice(0, 5).map(b => (
                                    <div
                                        key={b.BatchId}
                                        className={`filter-option ${activeFilter.type === "Batch" && activeFilter.id === b.BatchId ? "active" : ""}`}
                                        onClick={() => { setActiveFilter({ type: "Batch", id: b.BatchId, label: `Batch: ${b.BatchName}` }); setShowFilterDropdown(false); }}
                                    >
                                        {b.BatchName}
                                    </div>
                                ))}

                                <div className="filter-group-label">By Course</div>
                                {[...new Set(branchCourseList.flatMap(g => g.CourseList.map(c => c.CourseName)))].slice(0, 5).map(courseName => (
                                    <div
                                        key={courseName}
                                        className={`filter-option ${activeFilter.type === "Course" && activeFilter.id === courseName ? "active" : ""}`}
                                        onClick={() => { setActiveFilter({ type: "Course", id: courseName, label: `Course: ${courseName}` }); setShowFilterDropdown(false); }}
                                    >
                                        {courseName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="search-box">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="header-right">
                    <button className="add-student-btn" onClick={handleAddClick}>
                        <Plus size={20} />
                        <span>Add Student</span>
                    </button>
                </div>
            </div>

            {/* GRID */}
            <div className="student-grid-wrapper">
                {isLoading && (
                    <div className="loader-overlay">
                        <div className="loader-spinner"></div>
                        <p>Fetching Data...</p>
                    </div>
                )}
                <div className="table-scroll-area">
                    <table className="student-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Student Name</th>
                                <th>Parent Name</th>
                                <th>Phone Number</th>
                                <th>Course Name</th>
                                <th>Branch Name</th>
                                <th>Batch Name</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedStudents.length > 0 ? (
                                paginatedStudents.map((s, index) => (
                                    <tr key={s.StudentId}>
                                        <td><span className="id-badge">{(currentPage - 1) * itemsPerPage + index + 1}</span></td>
                                        <td>
                                            <div className="student-name-cell">
                                                <span>{s.Name}</span>
                                            </div>
                                        </td>
                                        <td>{s.ParentName || "N/A"}</td>
                                        <td>{s.PhoneNumber}</td>
                                        <td>
                                            <span className="course-badge">
                                                {s.CourseName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="branch-tag">
                                                {s.BranchName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="batch-badge">
                                                {s.BatchName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-cell">
                                                <button className="action-btn view" onClick={() => handleView(s)} title="View Profile">
                                                    <Eye size={18} />
                                                </button>
                                                <button className="action-btn edit" onClick={() => handleEditClick(s)} title="Edit Student">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button className="action-btn delete" onClick={() => handleDeleteClick(s)} title="Delete">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center" style={{ padding: '40px', color: '#94a3b8' }}>
                                        {isLoading ? "Loading students..." : "No students found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION CONTROLS */}
                {totalPages > 1 && (
                    <div className="pagination-container">
                        <div className="pagination-info">
                            Showing <span>{(currentPage - 1) * itemsPerPage + 1}</span> to <span>{Math.min(currentPage * itemsPerPage, filteredStudents.length)}</span> of <span>{filteredStudents.length}</span> students
                        </div>
                        <div className="pagination-btns">
                            <button
                                className="pag-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    className={`pag-num ${currentPage === i + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                className="pag-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>



            {/* ADD / EDIT MODAL */}
            {showAddModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-card wide student-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{isViewMode ? "View Student Details" : isEditMode ? "Edit Student" : "Register New Student"}</h3>
                            <button className="close-btn" onClick={handleCloseModal}><X size={20} /></button>
                        </div>
                        <div className="modal-body scrollable">
                            <div className="student-form-layout">
                                <div className="form-section">
                                    <h4><User size={16} /> Basic Information</h4>
                                    <div className="form-grid-inner">
                                        <FormInput label="Full Name" name="name" isAlpha value={formData.name} onChange={setFormData} icon={<User size={16} />} error={errors.name} setErrors={setErrors} readOnly={isViewMode} />
                                        <FormInput label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={setFormData} icon={<Calendar size={16} />} error={errors.dob} setErrors={setErrors} readOnly={isViewMode} />
                                        <FormInput label="Email Address (Optional)" name="email" type="email" value={formData.email} onChange={setFormData} icon={<Mail size={16} />} error={errors.email} setErrors={setErrors} readOnly={isViewMode} />
                                        <FormInput label="Phone Number" name="phoneNumber" isNumeric maxLength={10} value={formData.phoneNumber} onChange={setFormData} icon={<Phone size={16} />} error={errors.phoneNumber} setErrors={setErrors} readOnly={isViewMode} />
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h4><MapPin size={16} /> Academic & Batch Details</h4>
                                    <div className="form-grid-inner">
                                        <div className={`form-input-group ${errors.branchCourseId ? 'has-error' : ''}`}>
                                            <label>Branch & Course Mapping</label>
                                            <div
                                                className={`custom-select-wrapper ${isViewMode ? 'disabled' : ''} ${showMappingDropdown ? 'open' : ''} ${errors.branchCourseId ? 'error-border' : ''}`}
                                                onClick={() => !isViewMode && setShowMappingDropdown(!showMappingDropdown)}
                                            >
                                                <div className="selected-value">
                                                    {formData.branchCourseId ? (
                                                        branchCourseList.find(group => group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId)) ? (
                                                            `${branchCourseList.find(group => group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId))?.CourseName} - ${branchCourseList.find(group => group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.BranchName}`
                                                        ) : "Select Course - Branch"
                                                    ) : "Select Course - Branch"}
                                                </div>
                                                <ChevronRight size={18} className="select-arrow" />

                                                {showMappingDropdown && (
                                                    <div className="custom-dropdown-options">
                                                        <div
                                                            className="custom-option"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setFormData({ ...formData, branchCourseId: "" });
                                                                setErrors(prev => ({ ...prev, branchCourseId: "" }));
                                                                setShowMappingDropdown(false);
                                                            }}
                                                        >
                                                            Select Course - Branch
                                                        </div>
                                                        {branchCourseList.map((group, gIdx) => (
                                                            <div key={`branch-${group.BranchId}-${gIdx}`} className="option-group">
                                                                <div className="group-label">{group.BranchName}</div>
                                                                {group.CourseList.map((course, cIdx) => (
                                                                    <div
                                                                        key={`course-${course.BranchCourseId}-${cIdx}`}
                                                                        className={`custom-option ${String(formData.branchCourseId) === String(course.BranchCourseId) ? 'active' : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setFormData({ ...formData, branchCourseId: course.BranchCourseId });
                                                                            setErrors(prev => ({ ...prev, branchCourseId: "" }));
                                                                            setShowMappingDropdown(false);
                                                                        }}
                                                                    >
                                                                        {course.CourseName}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {errors.branchCourseId && <span className="error-message-inline">{errors.branchCourseId}</span>}
                                        </div>
                                        <FormInput label="Joining Date" name="joiningDate" type="date" value={formData.joiningDate} onChange={setFormData} error={errors.joiningDate} setErrors={setErrors} readOnly={isViewMode} />
                                        <div className={`form-input-group ${errors.batchId ? 'has-error' : ''}`}>
                                            <label>Batch Selection</label>
                                            <select
                                                value={formData.batchId}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, batchId: e.target.value });
                                                    setErrors(prev => ({ ...prev, batchId: "" }));
                                                }}
                                                disabled={isViewMode}
                                                className={`form-select-modern ${errors.batchId ? 'error-border' : ''} ${isViewMode ? 'readonly-input' : ''}`}
                                            >
                                                <option value="">Choose Batch</option>
                                                {batches.map((batch, bIdx) => (
                                                    <option key={`batch-${batch.BatchId}-${bIdx}`} value={batch.BatchId}>
                                                        {batch.BatchName} ({batch.BatchTime})
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.batchId && <span className="error-message-inline">{errors.batchId}</span>}
                                        </div>
                                        <FormInput label="Grade / Standard (Optional)" name="grade" value={formData.grade} onChange={setFormData} error={errors.grade} setErrors={setErrors} readOnly={isViewMode} />
                                    </div>

                                    <div className="level-picker-container">
                                        <label>Current Progress Level</label>
                                        <div className="progress-level-selector">
                                            {["Level-1", "Level-2", "Level-3", "Level-4", "Level-5"].map(level => (
                                                <button
                                                    key={level}
                                                    className={`level-pill ${formData.currentProgressLevel === level ? 'active' : ''} ${isViewMode ? 'disabled-pill' : ''}`}
                                                    onClick={() => !isViewMode && setFormData({ ...formData, currentProgressLevel: level })}
                                                >
                                                    {level}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="progress-bar-visual">
                                            <div
                                                className="progress-fill"
                                                style={{ width: `${(parseInt(formData.currentProgressLevel.split('-')[1]) / 5) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h4><CreditCard size={16} /> Fees & Security</h4>
                                    <div className="form-grid-inner">
                                        <FormInput label="Total Fees" name="fees" type="number" value={formData.fees} onChange={setFormData} error={errors.fees} setErrors={setErrors} readOnly={isViewMode} />
                                        <div className={`form-input-group`}>
                                            <label>Fee Recurrence</label>
                                            <select
                                                value={formData.feeRecurrence}
                                                onChange={(e) => setFormData({ ...formData, feeRecurrence: e.target.value })}
                                                disabled={isViewMode}
                                                className={`form-select-modern ${isViewMode ? 'readonly-input' : ''}`}
                                            >
                                                <option value="Monthly">Monthly</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Annually">Annually</option>
                                            </select>
                                        </div>
                                        <FormInput label="Parent Name (Optional)" name="Parentname" value={formData.Parentname} onChange={setFormData} error={errors.Parentname} setErrors={setErrors} readOnly={isViewMode} />
                                        {!isEditMode && (
                                            <div className={`form-input-group ${errors.password ? "has-error" : ""}`}>
                                                <label>Set 4-Digit PIN (Optional)</label>
                                                <div className="pin-digits-container">
                                                    {pin.map((digit, index) => (
                                                        <input
                                                            key={index}
                                                            id={`reg-pin-${index}`}
                                                            type="password"
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            maxLength={1}
                                                            value={digit}
                                                            onChange={(e) => !isViewMode && handlePinChange(e.target.value, index)}
                                                            onKeyDown={(e) => !isViewMode && handlePinKeyDown(e, index)}
                                                            className={`pin-digit-box ${errors.password ? "error-border" : ""} ${isViewMode ? "readonly-input" : ""}`}
                                                            autoComplete="new-password"
                                                            readOnly={isViewMode}
                                                        />
                                                    ))}
                                                </div>
                                                {errors.password && <span className="error-message-inline">{errors.password}</span>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={handleCloseModal}>{isViewMode ? "Close" : "Cancel"}</button>
                            {!isViewMode && (
                                <button className="save-student-btn-premium" onClick={handleSave}>
                                    {isEditMode ? "Update Student" : "Onboard Student"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DISCARD CHANGES CONFIRMATION */}
            {showDiscardModal && (
                <div className="modal-overlay" onClick={() => setShowDiscardModal(false)}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <AlertCircle size={48} color="#f59e0b" />
                            <h3>Discard Changes?</h3>
                            <p>You have unsaved changes. Are you sure you want to close? All details entered will be removed.</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowDiscardModal(false)}>Keep Editing</button>
                                <button className="delete-confirm-btn" style={{ background: '#f59e0b' }} onClick={confirmDiscard}>Discard</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE MODAL */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <AlertCircle size={48} color="#ef4444" />
                            <h3>Confirm Delete</h3>
                            <p>Are you sure you want to delete student <b>{selectedItem?.Name}</b>? This action is permanent.</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Keep Student</button>
                                <button className="delete-confirm-btn" onClick={confirmDelete}>Delete Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP */}
            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
                    <div className="popup-card" onClick={(e) => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>Got it</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ModernDetailItem = ({ icon, label, value, highlight, isBadge }) => (
    <div className={`modern-detail-item ${highlight ? `highlight-${highlight}` : ""}`}>
        <div className="detail-icon-wrap">{icon}</div>
        <div className="detail-content">
            <span className="detail-label">{label}</span>
            <span className={`detail-value ${isBadge ? "badge-value" : ""}`}>{value || "N/A"}</span>
        </div>
    </div>
);

const FormInput = ({ label, name, value, isAlpha, onChange, type = "text", icon, error, isNumeric, maxLength, setErrors, readOnly }) => (
    <div className={`form-input-group ${error ? "has-error" : ""}`}>
        <label>{label}</label>
        <div className={`input-with-icon ${error ? "error-border" : ""} ${readOnly ? "readonly-input-wrap" : ""}`}>
            {icon && <span className="input-icon">{icon}</span>}
            <input
                type={type}
                value={value}
                onChange={(e) => {
                    if (readOnly) return;
                    let val = e.target.value;
                    if (isNumeric) {
                        val = val.replace(/\D/g, "");
                    }

                    if (isAlpha) {
                        val = val.replace(/[0-9]/g, "");
                    }
                    if (maxLength && val.length > maxLength) {
                        val = val.slice(0, maxLength);
                    }
                    onChange(prev => ({ ...prev, [name]: val }));
                    if (setErrors) {
                        setErrors(prev => ({ ...prev, [name]: "" }));
                    }
                }}
                placeholder={readOnly ? "" : `Enter ${label.toLowerCase()}`}
                readOnly={readOnly}
                className={readOnly ? "readonly-input" : ""}
            />
        </div>
        {error && <span className="error-message-inline">{error}</span>}
    </div>
);

export default MasterStudent;
